import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { Text } from '@dealstack/frontend/shared/components/fundament/Text/Text';
import { Tooltip } from '@dealstack/frontend/shared/components/Tooltip/Tooltip';
import React from 'react';
import styled from 'styled-components';
import {
	IIconProps,
	PredefinedIcons,
} from '@dealstack/frontend/shared/components/fundament/Icon/Icon.types';

interface Props {
	'data-testid'?: string;
	className?: string;
	overtitle?: string;
	title: string | React.ReactNode;
	subtitle?: string | React.ReactNode;
	leftIcon?: PredefinedIcons;
	leftIconSize?: IIconProps['size'];
	rightIcon?: PredefinedIcons;
	tooltip?: string;
	toggled?: boolean;
	bold?: boolean;
	uppercaseOff?: boolean;
	onLeftIconClick?: () => unknown;
	onRightIconClick?: () => unknown;
	size?: 'h3b' | 'p-lg' | 'p-lgb' | 'h4b' | 'h5b' | 'p-sm';
}

const SectionHeader = ({
	className,
	title,
	size = 'h4b',
	tooltip,
	leftIcon,
	leftIconSize,
	rightIcon,
	onLeftIconClick,
	onRightIconClick,
	overtitle,
	toggled,
	uppercaseOff,
	subtitle,
	bold,
	'data-testid': dataTestId,
}: Props) => {
	const titleSize = getTitleSize(size);
	return (
		<div className={className}>
			<OverWrapper>
				<OverlineTitle colorFaded variant="overline" uppercase={!uppercaseOff}>
					{overtitle}
				</OverlineTitle>
			</OverWrapper>
			<MainWrapper>
				{leftIcon ? (
					<LeftIconWrapper>
						<Icon variant={leftIcon} size={leftIconSize} onClick={onLeftIconClick} />{' '}
					</LeftIconWrapper>
				) : null}

				<MainTitleWrapper data-testid={dataTestId}>
					{typeof title === 'string' ? (
						<Text
							variant={titleSize}
							bold={bold || ['h3b', 'h4b', 'h5b', 'p-lgb'].includes(size)}
						>
							{title}
						</Text>
					) : (
						title
					)}
				</MainTitleWrapper>

				{rightIcon ? (
					<RightIconWrapper>
						<Icon
							className="clickAnimation"
							variant={rightIcon}
							onClick={onRightIconClick}
						/>
					</RightIconWrapper>
				) : null}
				{tooltip ? <Tooltip tooltip={tooltip} /> : null}
				{toggled !== undefined ? (
					<Icon
						className="clickAnimation"
						variant={toggled ? 'chevron-top' : 'chevron-down'}
						onClick={onRightIconClick}
					/>
				) : null}
			</MainWrapper>
			<SubWrapper>
				{typeof subtitle === 'string' ? (
					<SubTitle variant="caption">{subtitle}</SubTitle>
				) : (
					subtitle
				)}
			</SubWrapper>
		</div>
	);
};

const getTitleSize = (size: Props['size']) => {
	switch (size) {
		case 'h3b':
			return 'h3';
		case 'p-lg':
		case 'p-lgb':
			return 'p-lg';
		case 'h4b':
			return 'h4';
		case 'h5b':
			return 'h5';
		default:
			return 'p-sm';
	}
};

const getIconSize = (size: Props['size']) => {
	switch (size) {
		case 'h3b':
			return '1rem';
		case 'p-lg':
		case 'p-lgb':
			return '1rem';
		case 'h4b':
			return '0.875rem';
		case 'h5b':
			return '0.875rem';
		default:
			return '0.75rem';
	}
};

const IconWrapper = styled.div`
	display: contents;
`;
const LeftIconWrapper = styled.div``;
const RightIconWrapper = styled.div``;
const MainTitleWrapper = styled.div``;
const OverlineTitle = styled(Text)``;
export const SubTitle = styled(Text)`
	color: ${({ theme }) => theme.v2.color.neutral[600]};
`;

const OverWrapper = styled.div`
	display: flex;
`;
const MainWrapper = styled.div`
	display: flex;
	align-items: center;
	${LeftIconWrapper} {
		margin-right: 0.75rem;
	}
	${RightIconWrapper} {
		margin-left: 0.75rem;
	}
`;
const SubWrapper = styled.div`
	display: flex;
`;

const Styled = styled(SectionHeader)`
	display: flex;
	flex-direction: column;
	white-space: pre-line;

	${LeftIconWrapper} ${Icon}.svg {
		--size: ${({ size, leftIconSize }) => (leftIconSize ? '' : getIconSize(size))};
	}

	${RightIconWrapper} ${Icon}.svg {
		--size: ${({ size }) => getIconSize(size)};
	}
	${IconWrapper} ${Icon} {
		margin-left: 0.75rem;
	}

	${MainWrapper} {
		margin-top: 0.25rem;
		margin-bottom: 0.25rem;
	}
	${MainWrapper} > ${Icon} {
		margin-left: 0.75rem;
	}
`;

export { Styled as SectionHeader };
